import React, {useEffect, useRef} from 'react';
import {observer} from 'mobx-react';
import classNames from 'classnames';
import styles from './minute-media-player.module.scss';
import {ErrorBoundaryWrapper} from '../../error-boundary/error-boundary-wrapper';
import {MINUTE_MEDIA_PLAYER_CONFIG} from './minute-media-player.consts';
import {UseDevice} from "../../composers/composer-plugins/use-device";

// Renders the Minute Media (SendToNews) player by mirroring the official embed snippet:
// a `[data-stn-player]` target plus the keyed loader script. The loader appends the Voltax
// engine, which locates the target via its CSS selector and injects the player into it.
//
// The target and the loader script are created imperatively (not as JSX) so they live
// outside React's reconciliation. React only owns the stable empty container; it never
// re-renders or removes the `[data-stn-player]` node or the DOM the engine injects into it.
// This matters on high-churn pages (e.g. the live-scores home page, which re-renders on
// every score delta): if React tore the target out of the DOM mid-injection, the engine's
// MutationObserver would drop it and fail with "no target element found".
export const MinuteMediaPlayer = observer(UseDevice()(({className, style, device}) => {
  const {direction, scriptBaseUrl} = MINUTE_MEDIA_PLAYER_CONFIG;
  const ref = useRef(null);
  const internalRef = useRef(null);


  useEffect(() => {
    if (!internalRef.current?.isConnected || !ref.current?.isConnected) {
      return;
    }

    const script = document.createElement('script');
    script.src = `${scriptBaseUrl}?mm-subId=${direction[device.direction] || direction.ltr}`;
    script.async = true;
    script.type = 'text/javascript';
    ref.current.appendChild(script);

    return () => {
      script.remove();
    };
  }, [device.direction, scriptBaseUrl]);


  return (
    <ErrorBoundaryWrapper>
      <div className={classNames(styles.minute_media_player, className)} style={style} ref={ref}>
        <div className={"s2nPlayer"} ref={internalRef}/>
      </div>
    </ErrorBoundaryWrapper>
  );
}));
